import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppFrame, Card, ProgressBar, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';
import { useAppStore } from '../store/AppStore';
import {
  TODAY_REWARD_BRICKS,
  TODAY_REWARD_POINTS,
  awardPuzzlePiece,
  claimPuzzleCompletionReward,
  claimTodayRewards,
  convertPuzzleDuplicates,
  createTodayVillageProgress,
  ensureToday,
  fallbackWeather,
  iglooRate,
  normalizePuzzleProgress,
  puzzleCatalog214,
  puzzleRate,
  selectPuzzlePiece,
  selectArticleQuestion,
  selectDailyArticle,
  usePuzzleHint,
} from '../domain/todayVillage';
import type { TodayVillageProgress } from '../domain/models';
import { iglooStages215, iglooZoneForStage215, iglooZones215, normalizeIglooProgress215 } from '../domain/iglooGrowth215';
import { buildFamilyExpeditionSignals216 } from '../domain/familyExpedition216';
import { buildMonthlyStory217 } from '../domain/village217';
import { fetchTodayContent, type TodayContentPayload } from '../services/todayContentApi';
import { appendRecommendationHistory218, recommendArticle218 } from '../domain/personalization218';


function useTodayContext() {
  const store = useAppStore();
  const state = store.state;
  const [remote,setRemote]=useState<TodayContentPayload|null>(null);
  const progress = state ? ensureToday((state as typeof state & { todayVillage?: TodayVillageProgress }).todayVillage, state.child.id) : null;
  const fallback = useMemo(() => fallbackWeather(new Date(), '서울특별시'), []);
  useEffect(()=>{
    const controller=new AbortController();
    void fetchTodayContent('서울특별시',controller.signal).then(setRemote).catch(()=>undefined);
    return ()=>controller.abort();
  },[]);
  const weather = remote?.weather ?? fallback;
  const fallbackArticle = state
    ? remote?.article ?? state.economicArticles.find((item)=>item.id===remote?.articleId) ?? selectDailyArticle(state.economicArticles, weather)
    : undefined;
  const recommendation = state ? recommendArticle218(state, weather, new Date(), fallbackArticle ? [fallbackArticle] : []) : undefined;
  const article = recommendation?.article ?? fallbackArticle;
  const question = state ? selectArticleQuestion(state.quizQuestions, article?.id) : undefined;
  const weatherContentMode:'live'|'server-fallback'|'client-fallback' = remote?.weatherSourceMode ?? (remote ? 'server-fallback' : 'client-fallback');
  return { ...store, state, progress, weather, article, question, recommendation, personalizedView:recommendation?.view, todayContentMode:remote?.sourceMode ?? 'client-fallback', weatherContentMode };
}

function Loading() {
  return <AppFrame title="오늘의 스노우빌리지"><div className="today-loading" role="status">오늘의 탐험을 준비하고 있어요…</div></AppFrame>;
}

function WeatherHero({ weather, sourceMode }: { weather: ReturnType<typeof fallbackWeather>; sourceMode: 'live' | 'server-fallback' | 'client-fallback' }) {
  const live = sourceMode === 'live';
  return <section className={`today-weather-hero weather-${weather.kind}`}>
    <div className="today-weather-copy">
      <p className="eyebrow">오늘의 스노우 마운틴</p>
      <div className="today-weather-line"><span aria-hidden="true">{weather.icon}</span><strong>{weather.region.replace('특별시','')} {weather.temperatureC}℃ · {weather.label}</strong></div>
      <h1>{weather.economyPrompt}</h1>
      {!live && <small>{sourceMode === 'server-fallback' ? '서버 대체 데이터' : '오프라인 대체 데이터'}</small>}
    </div>
    <GuideCharacter guide="snows" pose="guide" state="guide" alt={`${weather.label} 날씨의 경제 탐험을 안내하는 스노스`} priority />
    <div className="today-snow-ground" aria-hidden="true" />
  </section>;
}

export function TodayVillageHomeScreen() {
  const navigate = useNavigate();
  const { state, progress, weather, article, recommendation, personalizedView, patchState, weatherContentMode } = useTodayContext();
  if (!state || !progress) return <Loading />;
  const done = progress.status === 'complete';
  const pieces = progress.puzzle.acquiredPieceIds.length;
  const familySignals = buildFamilyExpeditionSignals216(state, state.familyExpedition);
  const monthlyStory = buildMonthlyStory217(state);
  return <AppFrame navigation="child" screenClass="today-village-home-2-13">
    <StatusBanner />
    <WeatherHero weather={weather} sourceMode={weatherContentMode} />
    <section className="today-quest-card" aria-label="오늘의 경제 탐험">
      <div className="today-quest-heading"><div><span>오늘의 경제 탐험</span><h2>{article?.title ?? '할인 상품은 항상 이득일까요?'}</h2></div><b>{done ? '완료' : '약 2분'}</b></div>
      <p>{article?.childSummary ?? '가격만 보지 말고 필요한 양과 선택 이유를 함께 살펴봐요.'}</p>
      <div className="today-keywords">{(article?.conceptIds ?? ['가격','선택']).slice(0,3).map((item) => <span key={item}>{item.replace('concept_','')}</span>)}</div>
      <div className="today-reward-preview"><span>🪙 최대 {TODAY_REWARD_POINTS}P</span><span>🧊 얼음 벽돌 {TODAY_REWARD_BRICKS}개</span><span>🧩 퍼즐 조각 1개</span></div>
      {recommendation && <div className="today-personalization-reason-2-18"><span>{personalizedView?.difficultyLabel} 추천</span><p>{recommendation.decision.reasons[0]}</p><Link to="/child/personalization">추천 설정</Link></div>}
      <button type="button" onClick={() => { if(done){ navigate('/village/hub'); return; } if(recommendation){ void patchState({ recommendationHistory:appendRecommendationHistory218(state.recommendationHistory,recommendation.decision) }); } navigate('/today/article'); }}>{done ? '성장한 마을 보기' : '오늘의 맞춤 탐험 시작하기'}<b>›</b></button>
    </section>

    <section className="today-growth-grid" aria-label="오늘의 성장">
      <Link to="/village" className="today-growth-card today-igloo-card">
        <span>나의 이글루</span><strong>{progress.igloo.currentStage}/36단계</strong>
        <div className={`mini-igloo stage-${Math.min(6,Math.ceil(progress.igloo.currentStage/6))}`} aria-hidden="true"><i/><em/></div>
        <ProgressBar value={iglooRate(progress)} />
        <small>다음 단계까지 벽돌 {Math.max(0, progress.igloo.nextRequirement - progress.igloo.totalBricks)}개</small>
      </Link>
      <Link to="/collection/puzzle" className="today-growth-card today-puzzle-card">
        <span>7월의 퍼즐</span><strong>{pieces}/{progress.puzzle.totalPieces}조각</strong>
        <div className="mini-puzzle-preview" aria-hidden="true">{Array.from({length:6},(_,index)=><i key={index} className={index < Math.min(pieces,6) ? 'filled':''}/>)}</div>
        <ProgressBar value={puzzleRate(progress)} />
        <small>{progress.puzzle.title}</small>
      </Link>
    </section>

    <section className="today-connected-growth-2-17" aria-label="연결된 성장">
      <Link to="/child/family-expedition" className="today-connected-family-2-17">
        <div><span>이번 주 가족 탐험</span><strong>{familySignals.completedCount}/4 신호</strong><small>{familySignals.complete ? '공동 보상을 받을 준비가 됐어요.' : `신호 ${4-familySignals.completedCount}개를 더 모아 보세요.`}</small></div>
        <div aria-hidden="true">{Array.from({length:4},(_,index)=><i key={index} className={index<familySignals.completedCount?'done':''}>{index<familySignals.completedCount?'✓':index+1}</i>)}</div>
      </Link>
      <Link to={monthlyStory.nextActionRoute} className={`today-connected-story-2-17 ${monthlyStory.completed?'complete':''}`}>
        <div><span>{monthlyStory.phaseLabel}</span><strong>{monthlyStory.chapterTitle}</strong><small>{monthlyStory.acquiredPieces}/{monthlyStory.totalPieces}조각 · {monthlyStory.nextActionLabel}</small></div><b>›</b>
      </Link>
    </section>

    <Card className="today-next-goal">
      <div><span>다음 목표</span><h2>{progress.igloo.currentStage >= 36 ? '스노우빌리지 랜드마크가 완성됐어요' : `${iglooStages215[progress.igloo.currentStage]?.name ?? '다음 단계'}까지 벽돌 ${Math.max(0, progress.igloo.nextRequirement-progress.igloo.totalBricks)}개`}</h2><p>포인트 숫자보다 내가 만든 변화가 먼저 보이도록 설계했어요.</p></div>
      <span className="today-resource-pill">🪙 {progress.points}P · 🧊 {progress.totalBricks}</span>
    </Card>
    <div className="today-version-links-2-18"><Link className="today-demo-link" to="/demo/2.18">MRM 2.18 개인화 탐험 데모</Link><Link to="/child/personalization/report">나의 맞춤 성장 리포트</Link></div>
  </AppFrame>;
}

export function TodayArticleScreen() {
  const navigate = useNavigate();
  const { state, progress, article, personalizedView, patchState, weather } = useTodayContext();
  if (!state || !progress) return <Loading />;
  const keyPoints = personalizedView?.keyPoints ?? article?.keyPoints ?? ['날씨는 생산량에 영향을 줘요.','시장에 나오는 양을 공급이라고 해요.','공급과 수요가 가격에 영향을 줘요.'];
  const start = async () => {
    await patchState({ todayVillage:{ ...progress, status:'article', updatedAt:new Date().toISOString() } });
    navigate('/today/quiz');
  };
  return <AppFrame title="오늘의 경제 탐험" backTo="/child/home" navigation="child" screenClass="today-flow-screen">
    <StatusBanner />
    <section className={`today-article-visual weather-${weather.kind}`}>
      <span aria-hidden="true">{weather.icon}</span>
      <div><small>오늘의 질문 · {personalizedView?.difficultyLabel ?? '기본'}</small><h2>{personalizedView?.title ?? article?.title ?? weather.economyPrompt}</h2></div>
    </section>
    {article && <p className="today-article-source">출처: {article.sourceUrl ? <a href={article.sourceUrl} target="_blank" rel="noreferrer">{article.sourceName}</a> : article.sourceName} · {article.sourcePublishedAt}</p>}
    <Card className="today-reading-card">
      <p className="eyebrow">3문장으로 이해해요</p>
      <ol>{keyPoints.slice(0,3).map((point,index)=><li key={point}><b>{index+1}</b><span>{point}</span></li>)}</ol>
    </Card>
    <div className="today-guide-note"><GuideCharacter guide="snows" pose="think" state="think" alt="기사의 핵심을 함께 생각하는 스노스" /><p>모든 문장을 외우기보다 <strong>무엇이 줄고, 무엇이 달라졌는지</strong> 찾아보세요.</p></div>
    <button className="primary-button" type="button" onClick={() => void start()}>퀴즈로 확인하기</button>
  </AppFrame>;
}

export function TodayQuizScreen() {
  const navigate = useNavigate();
  const { state, progress, question, patchState } = useTodayContext();
  const [choice, setChoice] = useState(progress?.selectedChoiceId ?? '');
  const [reason, setReason] = useState(progress?.reason ?? '');
  if (!state || !progress) return <Loading />;
  const choices = question?.choices ?? [
    {id:'a',label:'시장에 나오는 채소의 양이 줄어서'},
    {id:'b',label:'비 오는 날에는 모든 사람이 채소를 싫어해서'},
    {id:'c',label:'채소의 색이 달라져서'},
  ];
  const submit = async () => {
    await patchState({ todayVillage:{ ...progress, status:'quiz', selectedChoiceId:choice, reason:reason.trim(), updatedAt:new Date().toISOString() } });
    navigate('/today/mission');
  };
  return <AppFrame title="퀴즈와 나의 이유" backTo="/today/article" navigation="child" screenClass="today-flow-screen">
    <StatusBanner />
    <Card className="today-quiz-card">
      <p className="eyebrow">생각해 보기</p>
      <h2>{question?.prompt ?? '비가 많이 오면 채소 가격이 달라질 수 있는 가장 큰 이유는 무엇일까요?'}</h2>
      <div className="today-choice-list">{choices.map((item,index)=><button key={item.id} type="button" className={choice===item.id?'selected':''} onClick={()=>setChoice(item.id)}><b>{String.fromCharCode(65+index)}</b><span>{item.label}</span><i>{choice===item.id?'✓':''}</i></button>)}</div>
      <label className="today-reason-field"><span>선택한 이유를 한 문장으로 적어보세요.</span><textarea maxLength={120} value={reason} onChange={(event)=>setReason(event.target.value)} placeholder="시장에 나오는 양이 줄면…" /><small>{reason.length}/120</small></label>
    </Card>
    <div className="today-guide-note"><GuideCharacter guide="snows" pose="guide" state="quiet" alt="선택의 이유를 기다리는 스노스" /><p>정답보다 중요한 것은 <strong>내가 왜 그렇게 생각했는지</strong> 설명하는 힘이에요.</p></div>
    <button className="primary-button" type="button" disabled={!choice || reason.trim().length < 5} onClick={() => void submit()}>생활 미션으로</button>
  </AppFrame>;
}

export function TodayMissionScreen() {
  const navigate = useNavigate();
  const { state, progress, personalizedView, patchState } = useTodayContext();
  const [method,setMethod] = useState('record');
  if (!state || !progress) return <Loading />;
  const complete = async () => {
    await patchState({ todayVillage:{ ...progress, status:'reward', missionCompleted:true, updatedAt:new Date().toISOString() } });
    navigate('/today/reward');
  };
  return <AppFrame title="오늘의 생활 미션" backTo="/today/quiz" navigation="child" screenClass="today-flow-screen">
    <StatusBanner />
    <section className="today-mission-hero">
      <GuideCharacter guide="snows" pose="run" state="guide" alt="생활 미션을 제안하는 스노스" priority />
      <div><span>약 1분 · 맞춤 미션</span><h2>{personalizedView?.missionPrompt ?? '냉장고에서 오래된 식재료 한 가지를 찾아보세요.'}</h2><p>관심 분야와 선호 방식에 맞되, 보상 기준은 모든 사용자에게 동일해요.</p></div>
    </section>
    <Card className="today-proof-card">
      <p className="eyebrow">완료 방법을 선택해요</p>
      <div className="today-proof-options">
        {[['record','✍️','한 줄 기록'],['photo','📷','사진 체험'],['talk','💬','가족 대화'],['demo','✓','바로 완료']].map(([id,icon,label])=><button key={id} type="button" className={method===id?'selected':''} onClick={()=>setMethod(id)}><b>{icon}</b><span>{label}</span></button>)}
      </div>
      <p className="form-note">2.13에서는 자동 이미지 판정 대신 안전한 기본 제출 구조를 적용합니다.</p>
    </Card>
    <button className="primary-button" type="button" onClick={() => void complete()}>미션 완료하고 보상 보기</button>
  </AppFrame>;
}

export function TodayRewardScreen() {
  const navigate = useNavigate();
  const { state, progress, patchState } = useTodayContext();
  if (!state || !progress) return <Loading />;
  const claimed = progress.rewardClaimed;
  const claim = async () => {
    const next = claimTodayRewards(progress);
    await patchState({ todayVillage:next });
  };
  return <AppFrame title="오늘의 보상" backTo="/today/mission" navigation="child" screenClass="today-reward-screen">
    <StatusBanner />
    <section className="today-reward-hero">
      <div className="reward-confetti" aria-hidden="true">✦ · ❄ · ✦</div>
      <GuideCharacter guide="snows" pose="celebrate" state="celebrate" alt="오늘의 경제 탐험 완료를 축하하는 스노스" priority />
      <p className="eyebrow">탐험 완료</p><h1>{claimed ? '오늘의 생각이 성장으로 남았어요!' : '오늘의 보상을 받아보세요!'}</h1>
    </section>
    <div className="today-reward-list">
      <article><span>🪙</span><div><small>스노우 포인트</small><strong>+{TODAY_REWARD_POINTS}P</strong></div></article>
      <article><span>🧊</span><div><small>얼음 벽돌</small><strong>+{TODAY_REWARD_BRICKS}</strong></div></article>
      <article><span>🧩</span><div><small>퍼즐 조각</small><strong>+1</strong></div></article>
    </div>
    {claimed && <Card className="reward-growth-result">
      <div><span>이글루 성장</span><strong>{progress.igloo.currentStage}/36단계</strong></div>
      <ProgressBar value={iglooRate(progress)} />
      <p>{iglooStages215[Math.max(0,progress.igloo.currentStage-1)]?.message}</p>
    </Card>}
    {!claimed
      ? <button className="primary-button" type="button" onClick={() => void claim()}>보상 받기</button>
      : <button className="primary-button" type="button" onClick={() => navigate('/village')}>성장한 이글루 보기</button>}
    <Link className="secondary-button link-button" to="/collection/puzzle">받은 퍼즐 조각 확인</Link>
  </AppFrame>;
}

export function VillageIglooScreen() {
  const { state, progress } = useTodayContext();
  if (!state || !progress) return <Loading />;
  const igloo=normalizeIglooProgress215(progress.igloo,progress.totalBricks);
  const current=iglooStages215[igloo.currentStage-1];
  const zone=iglooZoneForStage215(igloo.currentStage);
  const remaining=Math.max(0,igloo.nextRequirement-igloo.totalBricks);
  const wallet=[
    ['ice','일반','🧊'],['family','가족','💗'],['knowledge','지식','📘'],['sharing','공유','🤝'],['challenge','도전','🏅'],
  ] as const;
  return <AppFrame title="나의 이글루" backTo="/child/home" navigation="child" screenClass="igloo-growth-screen-2-15">
    <section className="igloo-current-hero igloo-current-hero-2-15">
      <div><p className="eyebrow">MRM 2.15 · {zone.title}</p><h1>{igloo.currentStage}단계 / 36<br/>{current.name}</h1><p>{current.message}</p><span>🧊 누적 벽돌 {igloo.totalBricks}개 · 다음 단계까지 {remaining}개</span></div>
      <div className={`large-igloo stage-${Math.min(6,Math.ceil(igloo.currentStage/6))} tier-${Math.ceil(igloo.currentStage/6)}`} aria-label={`${igloo.currentStage}단계 ${current.name}`}><i/><em/><b/></div>
    </section>

    <section className="igloo-zone-progress-2-15" aria-label="이글루 성장 구간">
      {iglooZones215.map((item,index)=>{const first=index*6+1;const last=first+5;const complete=igloo.currentStage>last;const active=igloo.currentStage>=first&&igloo.currentStage<=last;return <article key={item.id} className={`${complete?'complete':''} ${active?'active':''}`}><span>{item.range}단계</span><strong>{item.title}</strong><small>{item.emotion}</small>{complete&&<b>✓</b>}</article>;})}
    </section>

    <Card className="igloo-brick-wallet-2-15">
      <div className="igloo-wallet-heading"><div><span>벽돌 자원</span><h2>행동의 성격이 다른 벽돌로 남아요</h2></div><strong>{igloo.totalBricks}개</strong></div>
      <div className="igloo-wallet-grid">{wallet.map(([key,label,icon])=><article key={key}><span>{icon}</span><small>{label} 벽돌</small><strong>{igloo.brickWallet[key]}</strong></article>)}</div>
    </Card>

    <section className="igloo-roadmap-2-15" aria-label="이글루 1단계부터 36단계">
      {iglooZones215.map((group,index)=><section key={group.id} className={`igloo-roadmap-zone zone-${group.id}`}><header><span>{group.range}단계</span><div><h2>{group.title}</h2><p>{group.emotion}</p></div></header><div>{iglooStages215.slice(index*6,index*6+6).map((item)=>{const active=item.stage===igloo.currentStage;const complete=item.stage<=igloo.currentStage;return <article key={item.stage} className={`${complete?'complete':''} ${active?'active':''}`}><span>{item.stage}</span><div className={`roadmap-igloo stage-${Math.min(6,index+1)}`}><i/><em/></div><h3>{item.name}</h3><small>누적 {item.requirement}개</small>{item.featureLabel&&<p>{complete?'해금':'예정'} · {item.featureLabel}</p>}{complete&&<b>✓</b>}</article>;})}</div></section>)}
    </section>

    <Card className="igloo-unlock-list-2-15"><div><span>현재 해금 기능</span><h2>{igloo.unlockedFeatureIds.length}개 기능이 열렸어요</h2></div><ul>{iglooStages215.filter((item)=>item.featureId&&igloo.unlockedFeatureIds.includes(item.featureId)).map((item)=><li key={item.featureId}><b>{item.stage}단계</b><span>{item.featureLabel}</span></li>)}</ul></Card>
    <Link className="today-demo-home-link" to="/demo/2.15/igloo">36단계 이글루 데모 보기</Link>
  </AppFrame>;
}

export function IglooDemoScreen215() {
  const milestones=[3,12,18,24,30,36];
  const [index,setIndex]=useState(0);
  const stage=milestones[index];
  const item=iglooStages215[stage-1];
  const zone=iglooZoneForStage215(stage);
  const reset=()=>setIndex(0);
  return <AppFrame title="MRM 2.15 이글루 데모" backTo="/demo" screenClass="igloo-demo-screen-2-15">
    <div className="demo-sample-banner">체험용 샘플 데이터 · 실제 계정과 완전 분리</div>
    <section className="igloo-demo-hero-2-15"><GuideCharacter guide="snows" pose={stage===36?'celebrate':'guide'} state={stage===36?'celebrate':'guide'} alt={`${stage}단계 이글루를 안내하는 스노스`} priority/><div><p>핵심 단계 {index+1} / {milestones.length}</p><h1>{stage}단계 · {item.name}</h1><strong>{zone.title}</strong><span>{item.requirement}개 벽돌 · {item.message}</span></div></section>
    <section className="igloo-demo-building-2-15"><div className={`large-igloo stage-${Math.min(6,Math.ceil(stage/6))} tier-${Math.ceil(stage/6)}`} aria-label={`${stage}단계 ${item.name}`}><i/><em/><b/></div><div><span>성장 진행</span><strong>{stage} / 36</strong><ProgressBar value={Math.round(stage/36*100)}/>{item.featureLabel&&<p>해금 기능 · {item.featureLabel}</p>}</div></section>
    <section className="igloo-demo-milestones-2-15">{milestones.map((value)=><button key={value} type="button" className={value===stage?'active':''} onClick={()=>setIndex(milestones.indexOf(value))}><span>{value}단계</span><strong>{iglooStages215[value-1].name}</strong></button>)}</section>
    <div className="demo-213-actions"><button type="button" className="secondary-button" onClick={reset}>초기화</button><button type="button" className="primary-button" onClick={()=>setIndex((value)=>Math.min(milestones.length-1,value+1))} disabled={index===milestones.length-1}>{index===milestones.length-1?'체험 완료':'다음 성장'}</button></div>
  </AppFrame>;
}

export function MiniPuzzleScreen() {
  const { state, progress, patchState } = useTodayContext();
  if (!state || !progress) return <Loading />;
  const puzzle=normalizePuzzleProgress(progress.puzzle);
  const acquired = puzzle.acquiredPieceIds.length;
  const updatePuzzle=async(nextPuzzle:typeof puzzle)=>patchState({ todayVillage:{ ...progress, puzzle:nextPuzzle, updatedAt:new Date().toISOString() } });
  const selectPiece=(pieceId:string)=>void updatePuzzle(selectPuzzlePiece(puzzle,pieceId));
  const useHint=()=>void updatePuzzle(usePuzzleHint(puzzle));
  const convertDuplicates=()=>void updatePuzzle(convertPuzzleDuplicates(puzzle));
  const claimStory=()=>void updatePuzzle(claimPuzzleCompletionReward(puzzle));
  return <AppFrame title="퍼즐 컬렉션" backTo="/child/home" navigation="child" screenClass="puzzle-system-screen-2-14">
    <section className="puzzle-story-hero"><div><p className="eyebrow">MRM 2.14 · 월간 스토리 퍼즐</p><h1>{puzzle.title}</h1><p>조각의 획득 이유가 기록되고, 완성 장면은 이글루 갤러리에 오래 보관돼요.</p></div><GuideCharacter guide="snows" pose="research" state="guide" alt="퍼즐 이야기를 연구하는 스노스" /></section>

    <section className="puzzle-type-strip" aria-label="퍼즐 유형">
      {puzzleCatalog214.map((item)=><article key={item.id} className={item.id===puzzle.puzzleId?'active':''}><span>{item.type==='daily'?'일일':item.type==='weekly'?'주간':item.type==='monthly'?'월간':item.type==='seasonal'?'시즌':'이벤트'}</span><strong>{item.title}</strong><small>{item.totalPieces}조각</small></article>)}
    </section>

    <Card className="puzzle-resource-card">
      <div><span>💡 힌트 토큰</span><strong>{puzzle.hintTokens}</strong></div>
      <div><span>♻️ 중복 조각</span><strong>{puzzle.duplicateCredits}</strong></div>
      <div><span>🎟️ 스토리 티켓</span><strong>{puzzle.storyTickets}</strong></div>
      <button type="button" onClick={useHint} disabled={puzzle.hintTokens<1||puzzle.completed}>힌트 사용</button>
      <button type="button" onClick={convertDuplicates} disabled={puzzle.duplicateCredits<2}>중복 2개 교환</button>
    </Card>

    <div className="puzzle-board-heading"><div><p className="eyebrow">다음 조각을 직접 선택해요</p><h2>{acquired}/{puzzle.totalPieces}조각</h2></div><ProgressBar value={puzzleRate({...progress,puzzle})} /></div>
    <section className="puzzle-board-2-14" aria-label={`퍼즐 ${acquired}/${puzzle.totalPieces}조각`}>
      {Array.from({length:puzzle.totalPieces},(_,index)=>{
        const id=`piece_${String(index+1).padStart(2,'0')}`;
        const owned=puzzle.acquiredPieceIds.includes(id);
        const selected=puzzle.selectedPieceId===id;
        const hinted=puzzle.hintedPieceId===id;
        const record=puzzle.pieceRecords.find((item)=>item.pieceId===id);
        return <button key={id} type="button" disabled={owned||puzzle.completed} className={`${owned?'acquired':'locked'} ${selected?'selected':''} ${hinted?'hinted':''} rarity-${record?.rarity??'common'}`} aria-pressed={selected} onClick={()=>selectPiece(id)}>
          <span>{owned?(record?.rarity==='gold'?'★':record?.rarity==='rare'?'❄':'◆'):hinted?'!':'?'}</span><small>{index+1}</small>{selected&&<b>선택</b>}
        </button>;
      })}
    </section>
    <p className="puzzle-selection-note">{puzzle.completed?'모든 조각을 모았어요. 아래에서 완성 이야기를 열어보세요.':puzzle.selectedPieceId?`${Number(puzzle.selectedPieceId.replace(/\D/g,''))}번 조각을 다음 보상으로 선택했어요.`:'원하는 잠긴 조각을 선택하거나 힌트를 사용해 보세요.'}</p>

    {puzzle.storyUnlocked && <section className="puzzle-story-unlock"><GuideCharacter guide="snows" pose="celebrate" state="celebrate" alt="퍼즐 완성을 축하하는 스노스" /><div><p className="eyebrow">장면 완성</p><h2>설옹의 비밀 금고가 열렸어요</h2><p>은행의 진짜 힘은 금고가 아니라 서로의 약속을 지키는 신뢰에 있었어요.</p>{puzzle.completionRewardClaimed?<strong>스토리 티켓 보관 완료</strong>:<button type="button" className="primary-button" onClick={claimStory}>스토리 티켓 받기</button>}</div></section>}

    <section className="puzzle-source-list"><h2>조각 획득 기록</h2>{puzzle.pieceRecords.map((record)=><article key={record.pieceId}><b>조각 {record.position}</b><span>{record.sourceLabel}</span><small>{record.rarity==='gold'?'골드':record.rarity==='rare'?'희귀':'일반'} · 보관 완료</small></article>)}</section>
  </AppFrame>;
}

export function PuzzleDemoScreen214() {
  const base=normalizePuzzleProgress({ ...createTodayVillageProgress('demo_child').puzzle, acquiredPieceIds:Array.from({length:9},(_,index)=>`piece_${String(index+1).padStart(2,'0')}`), pieceRecords:[], selectedPieceId:'', hintedPieceId:'', hintTokens:1, duplicateCredits:2 });
  const [puzzle,setPuzzle]=useState(base);
  const [step,setStep]=useState(0);
  const applyNext=()=>{
    if (step===0) setPuzzle(selectPuzzlePiece(puzzle,'piece_10'));
    if (step===1) setPuzzle(usePuzzleHint(puzzle));
    if (step===2) setPuzzle(awardPuzzlePiece(puzzle,'데모 경제 미션 완료'));
    if (step===3) {
      let next=puzzle;
      while (!next.completed) next=awardPuzzlePiece(next,'데모 자동 완성');
      setPuzzle(next);
    }
    if (step===4) setPuzzle(claimPuzzleCompletionReward(puzzle));
    setStep((value)=>Math.min(5,value+1));
  };
  const reset=()=>{setPuzzle(base);setStep(0);};
  const messages=['10번 조각을 직접 선택해요.','힌트 토큰으로 추천 조각을 확인해요.','선택한 조각을 보상으로 획득해요.','남은 조각을 채워 장면을 완성해요.','스토리 티켓을 보관해요.','퍼즐 데모가 완료됐어요.'];
  return <AppFrame title="MRM 2.14 퍼즐 데모" backTo="/demo" screenClass="puzzle-demo-screen-2-14">
    <div className="demo-sample-banner">체험용 샘플 데이터 · 실제 계정과 완전 분리</div>
    <section className="puzzle-demo-summary"><GuideCharacter guide="snows" pose={step>=4?'celebrate':'research'} state={step>=4?'celebrate':'guide'} alt="퍼즐 데모를 안내하는 스노스" priority /><div><p>STEP {step+1} / 6</p><h1>{messages[step]}</h1><span>{puzzle.acquiredPieceIds.length}/{puzzle.totalPieces}조각 · 힌트 {puzzle.hintTokens} · 티켓 {puzzle.storyTickets}</span></div></section>
    <section className="puzzle-board-2-14 demo" aria-label="퍼즐 데모 보드">{Array.from({length:puzzle.totalPieces},(_,index)=>{const id=`piece_${String(index+1).padStart(2,'0')}`;const owned=puzzle.acquiredPieceIds.includes(id);return <div key={id} className={`${owned?'acquired':'locked'} ${puzzle.selectedPieceId===id?'selected':''} ${puzzle.hintedPieceId===id?'hinted':''}`}><span>{owned?'◆':puzzle.hintedPieceId===id?'!':'?'}</span><small>{index+1}</small></div>;})}</section>
    <div className="demo-213-actions"><button type="button" className="secondary-button" onClick={reset}>초기화</button><button type="button" className="primary-button" onClick={applyNext} disabled={step===5}>{step===5?'체험 완료':'다음 단계'}</button></div>
  </AppFrame>;
}

export function TodayVillageDemoScreen() {
  const [step,setStep]=useState(0);
  const [resetKey,setResetKey]=useState(0);
  const steps = [
    ['오늘의 홈','비 오는 날씨와 경제 질문을 발견해요.','🌧️'],
    ['기사 탐험','3문장으로 공급과 가격의 관계를 이해해요.','📰'],
    ['퀴즈와 이유','정답과 함께 나의 선택 이유를 기록해요.','💭'],
    ['생활 미션','냉장고의 오래된 식재료를 찾아봐요.','🥬'],
    ['보상 획득','23P·벽돌 1개·퍼즐 조각 1개를 받아요.','🎁'],
    ['이글루 성장','벽돌이 놓이고 다음 건설 목표가 보여요.','🏠'],
    ['퍼즐 보관','획득 이유가 기록된 조각을 컬렉션에 보관해요.','🧩'],
  ];
  const current=steps[step];
  const reset=()=>{setStep(0);setResetKey((value)=>value+1);};
  return <AppFrame title="MRM 2.13 데모" backTo="/demo" screenClass="today-demo-screen-2-13">
    <div className="demo-sample-banner">체험용 샘플 데이터 · 실제 계정과 분리</div>
    <section key={resetKey} className="demo-213-stage">
      <span>{current[2]}</span><p>STEP {step+1} / {steps.length}</p><h1>{current[0]}</h1><p>{current[1]}</p>
      <GuideCharacter guide="snows" pose={step===4?'celebrate':step===2?'think':'guide'} state={step===4?'celebrate':'guide'} alt={`${current[0]}을 안내하는 스노스`} priority />
      <div className="demo-213-progress">{steps.map((_,index)=><i key={index} className={index<=step?'active':''}/>)}</div>
    </section>
    <div className="demo-213-actions"><button type="button" className="secondary-button" onClick={reset}>초기화</button><button type="button" className="primary-button" onClick={()=>setStep((value)=>Math.min(steps.length-1,value+1))} disabled={step===steps.length-1}>{step===steps.length-1?'체험 완료':'다음 단계'}</button></div>
    <div className="today-demo-links"><Link className="today-demo-home-link" to="/child/home">실제 홈 구조 보기</Link><Link className="today-demo-home-link" to="/demo/2.14/puzzle">2.14 퍼즐 데모 보기</Link><Link className="today-demo-home-link" to="/demo/2.15/igloo">2.15 이글루 데모 보기</Link><Link className="today-demo-home-link" to="/demo/2.16/family">2.16 가족 탐험대 데모 보기</Link><Link className="today-demo-home-link" to="/demo/2.17">2.17 통합 마을 데모 보기</Link></div>
  </AppFrame>;
}
